import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  TrendingUp,
  Wallet,
  Copy,
  LogOut,
  Loader2,
  Lock,
  Sparkles,
  Users,
  Check,
  DollarSign,
  Clock,
  X as XIcon,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";

const TOKEN_KEY = "boostup_aff_token";

const COMMISSION_TABLE = [
  { pkg: "1K Followers",  rate: "$1" },
  { pkg: "2.5K Followers", rate: "$2" },
  { pkg: "5K Followers",  rate: "$5" },
  { pkg: "10K Followers", rate: "$10" },
  { pkg: "25K Followers", rate: "$20" },
  { pkg: "50K Followers", rate: "$30" },
];

const RegisterForm = ({ onAuth, switchToLogin }) => {
  const [form, setForm] = useState({ email: "", password: "", promo_code: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/affiliate/register", {
        email: form.email.trim(),
        password: form.password,
        promo_code: form.promo_code.trim().toUpperCase(),
      });
      onAuth(r.data.token, r.data.affiliate);
      toast.success(`Welcome aboard! Your code is ${r.data.affiliate.promo_code}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign up failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="affiliate-register-form" className="space-y-4">
      <Field label="Email" testId="register-email">
        <Input
          type="email"
          required
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="you@email.com"
          className="bg-transparent border-white/10 text-white focus:border-[#FF007F] focus:ring-1 focus:ring-[#FF007F]"
        />
      </Field>
      <Field label="Password" testId="register-password">
        <Input
          type="password"
          required
          minLength={6}
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          placeholder="At least 6 characters"
          className="bg-transparent border-white/10 text-white focus:border-[#FF007F] focus:ring-1 focus:ring-[#FF007F]"
        />
      </Field>
      <Field label="Your promo code" testId="register-promo">
        <Input
          type="text"
          required
          value={form.promo_code}
          onChange={(e) => setForm({ ...form, promo_code: e.target.value.toUpperCase() })}
          placeholder="LIKE10"
          maxLength={20}
          className="bg-transparent border-white/10 text-white focus:border-[#FF007F] focus:ring-1 focus:ring-[#FF007F] uppercase tracking-widest font-mono"
        />
        <p className="text-xs text-white/40 mt-1">
          4–20 characters, A–Z and 0–9 only. This is what you'll share with friends.
        </p>
      </Field>
      <button
        type="submit"
        data-testid="affiliate-register-submit"
        disabled={busy}
        className="w-full bg-neon text-white font-bold py-3 rounded-full hover:scale-[1.02] transition-transform glow-pink disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        Create affiliate account
      </button>
      <p className="text-center text-sm text-white/50">
        Already have an account?{" "}
        <button
          type="button"
          onClick={switchToLogin}
          className="text-[#FF007F] hover:text-[#FF5E00] font-semibold"
        >
          Log in
        </button>
      </p>
    </form>
  );
};

const LoginForm = ({ onAuth, switchToRegister }) => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/affiliate/login", form);
      onAuth(r.data.token, r.data.affiliate);
      toast.success("Welcome back!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="affiliate-login-form" className="space-y-4">
      <Field label="Email" testId="login-email">
        <Input
          type="email"
          required
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="you@email.com"
          className="bg-transparent border-white/10 text-white focus:border-[#FF007F] focus:ring-1 focus:ring-[#FF007F]"
        />
      </Field>
      <Field label="Password" testId="login-password">
        <Input
          type="password"
          required
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          className="bg-transparent border-white/10 text-white focus:border-[#FF007F] focus:ring-1 focus:ring-[#FF007F]"
        />
      </Field>
      <button
        type="submit"
        data-testid="affiliate-login-submit"
        disabled={busy}
        className="w-full bg-neon text-white font-bold py-3 rounded-full hover:scale-[1.02] transition-transform glow-pink disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        Log in
      </button>
      <p className="text-center text-sm text-white/50">
        Don't have an account?{" "}
        <button
          type="button"
          onClick={switchToRegister}
          className="text-[#FF007F] hover:text-[#FF5E00] font-semibold"
        >
          Sign up
        </button>
      </p>
    </form>
  );
};

const Field = ({ label, children, testId }) => (
  <div data-testid={testId}>
    <label className="text-xs uppercase tracking-[0.2em] font-bold text-white/60 mb-2 block">
      {label}
    </label>
    {children}
  </div>
);

const StatCard = ({ label, value, accent, testId }) => (
  <div data-testid={testId} className="bg-[#0A0A0A] border border-white/10 rounded-xl p-6">
    <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2">{label}</div>
    <div className={`font-display text-3xl font-black ${accent || ""}`}>{value}</div>
  </div>
);

const Dashboard = ({ token, affiliate: initialAff, onLogout }) => {
  const [aff, setAff] = useState(initialAff);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [payoutForm, setPayoutForm] = useState({ cashapp_tag: "", note: "" });
  const [submitting, setSubmitting] = useState(false);

  const headers = { headers: { Authorization: `Bearer ${token}` } };
  const refLink = `${window.location.origin}/?ref=${aff.promo_code}`;

  const load = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        api.get("/affiliate/stats", headers),
        api.get("/affiliate/payout-requests", headers).catch(() => ({ data: [] })),
      ]);
      setStats(s.data);
      setAff(s.data.affiliate);
      setRequests(r.data);
    } catch (e) {
      if (e?.response?.status === 401) onLogout();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const minPayout = stats?.min_payout_usd ?? 20;
  const pendingRequested = requests
    .filter((r) => r.status === "pending")
    .reduce((s, r) => s + r.amount, 0);
  const available = Math.max(0, aff.pending_payout - pendingRequested);
  const canRequest = available >= minPayout;

  const submitPayout = async (e) => {
    e.preventDefault();
    let tag = payoutForm.cashapp_tag.trim();
    if (!tag) {
      toast.error("Enter your CashApp tag");
      return;
    }
    if (!tag.startsWith("$")) tag = `$${tag}`;
    setSubmitting(true);
    try {
      await api.post(
        "/affiliate/payout-request",
        { cashapp_tag: tag, note: payoutForm.note },
        headers
      );
      toast.success("Payout request sent! We'll DM you on Instagram once approved.");
      setPayoutOpen(false);
      setPayoutForm({ cashapp_tag: "", note: "" });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="flex-1 max-w-6xl mx-auto w-full px-6 lg:px-10 py-16">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
        <div>
          <div className="uppercase text-xs font-bold tracking-[0.2em] text-[#FF007F] mb-3">
            Affiliate program
          </div>
          <h1 className="font-display text-4xl font-black tracking-tighter">
            Welcome, <span className="text-gradient-neon">{aff.email.split("@")[0]}</span>
          </h1>
        </div>
        <button
          data-testid="affiliate-logout-btn"
          onClick={onLogout}
          className="inline-flex items-center gap-2 bg-white/5 border border-white/10 text-white/70 px-4 py-2.5 rounded-full hover:bg-white/10 hover:text-white transition text-sm self-start md:self-auto"
        >
          <LogOut className="w-4 h-4" /> Log out
        </button>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[#0A0A0A] border border-[#FF007F]/30 rounded-2xl p-8 mb-10 glow-pink"
      >
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF007F] mb-3">
          Your promo code
        </div>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div data-testid="affiliate-code" className="font-display text-5xl md:text-6xl font-black tracking-wider text-gradient-neon">
              {aff.promo_code}
            </div>
            <p className="text-white/60 text-sm mt-3 max-w-md">
              Anyone who enters this code at checkout gets <strong className="text-white">$1 off</strong>, and you earn a commission per package.
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <button
              data-testid="copy-code-btn"
              onClick={() => copy(aff.promo_code, "Code")}
              className="inline-flex items-center gap-2 bg-white/5 border border-white/10 text-white px-5 py-3 rounded-full hover:bg-white/10 transition text-sm"
            >
              <Copy className="w-4 h-4" /> Copy code
            </button>
            <button
              data-testid="copy-link-btn"
              onClick={() => copy(refLink, "Referral link")}
              className="inline-flex items-center gap-2 bg-neon text-white px-5 py-3 rounded-full hover:scale-[1.02] transition text-sm font-bold"
            >
              <Sparkles className="w-4 h-4" /> Copy referral link
            </button>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <StatCard
          label="Pending payout"
          value={`$${aff.pending_payout.toFixed(2)}`}
          accent="text-gradient-neon"
          testId="stat-pending"
        />
        <StatCard
          label="Total earned"
          value={`$${aff.total_earned.toFixed(2)}`}
          testId="stat-earned"
        />
        <StatCard
          label="Paid orders"
          value={stats?.paid_orders ?? 0}
          testId="stat-orders"
        />
        <StatCard
          label="Code clicks"
          value={stats?.clicks ?? 0}
          testId="stat-clicks"
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-[#0A0A0A] border border-white/10 rounded-2xl p-8">
          <div className="flex items-center gap-2 mb-6">
            <Users className="w-4 h-4 text-[#FF007F]" />
            <h3 className="font-display text-xl font-black tracking-tight">Recent referrals</h3>
          </div>
          {loading && !stats ? (
            <div className="flex items-center gap-2 text-white/40 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : stats?.recent_orders?.length ? (
            <ul className="divide-y divide-white/5">
              {stats.recent_orders.map((o, i) => (
                <li key={i} className="py-3 flex items-center justify-between text-sm">
                  <div className="flex flex-col">
                    <span className="font-mono text-white">@{o.instagram_username}</span>
                    <span className="text-white/40 text-xs">
                      {o.package_label} · {new Date(o.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    {o.commission_amount ? (
                      <span className="text-gradient-neon font-bold">
                        +${o.commission_amount.toFixed(2)}
                      </span>
                    ) : (
                      <span className="text-white/30 text-xs">—</span>
                    )}
                    <span
                      className={`text-[10px] uppercase font-bold tracking-[0.15em] px-2 py-0.5 rounded-full border ${
                        o.payment_status === "paid"
                          ? "bg-[#FF007F]/10 text-[#FF007F] border-[#FF007F]/30"
                          : "bg-white/5 text-white/60 border-white/10"
                      }`}
                    >
                      {o.payment_status}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-white/40 text-sm">
              No referrals yet. Share your code or link to start earning.
            </p>
          )}
        </div>

        <div className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-8">
          <div className="flex items-center gap-2 mb-6">
            <TrendingUp className="w-4 h-4 text-[#FF5E00]" />
            <h3 className="font-display text-xl font-black tracking-tight">Commission rates</h3>
          </div>
          <ul className="space-y-2 text-sm">
            {COMMISSION_TABLE.map((row) => (
              <li key={row.pkg} className="flex justify-between border-b border-white/5 pb-2">
                <span className="text-white/70">{row.pkg}</span>
                <span className="text-white font-bold">{row.rate}</span>
              </li>
            ))}
          </ul>

          <div className="mt-6 pt-6 border-t border-white/10">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-white/40 mb-3">
              Cash out
            </div>
            <div className="text-xs text-white/50 mb-4 leading-relaxed">
              {canRequest
                ? `You have $${available.toFixed(2)} ready to request.`
                : `You need $${minPayout.toFixed(2)} minimum to cash out. You have $${available.toFixed(2)}.`}
            </div>
            <button
              data-testid="request-payout-btn"
              disabled={!canRequest}
              onClick={() => setPayoutOpen(true)}
              className="w-full inline-flex items-center justify-center gap-2 bg-neon text-white font-bold py-3 rounded-full hover:scale-[1.02] transition-transform glow-pink disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              <Wallet className="w-4 h-4" />
              {canRequest ? "Request payout" : `Locked until $${minPayout.toFixed(0)}`}
            </button>
            {pendingRequested > 0 && (
              <div className="text-[11px] text-white/40 mt-3 text-center">
                <Clock className="w-3 h-3 inline mr-1 -mt-0.5" />
                ${pendingRequested.toFixed(2)} request{pendingRequested === 1 ? "" : "s"} awaiting review
              </div>
            )}
          </div>
        </div>
      </div>

      {requests.length > 0 && (
        <div className="mt-6 bg-[#0A0A0A] border border-white/10 rounded-2xl p-8">
          <div className="flex items-center gap-2 mb-6">
            <Wallet className="w-4 h-4 text-[#FF007F]" />
            <h3 className="font-display text-xl font-black tracking-tight">Payout requests</h3>
          </div>
          <ul className="divide-y divide-white/5">
            {requests.map((r) => (
              <li key={r.id} className="py-3 flex items-center justify-between gap-4 text-sm">
                <div className="flex flex-col">
                  <span className="text-white font-bold">${r.amount.toFixed(2)}</span>
                  <span className="text-white/40 text-xs">
                    {new Date(r.created_at).toLocaleString()} · {r.cashapp_tag}
                  </span>
                  {r.admin_note && (
                    <span className="text-red-300/80 text-xs mt-1">Admin note: {r.admin_note}</span>
                  )}
                </div>
                <span
                  className={`text-[10px] uppercase font-bold tracking-[0.15em] px-2.5 py-1 rounded-full border whitespace-nowrap ${
                    r.status === "pending"
                      ? "bg-[#FF5E00]/10 text-[#FF5E00] border-[#FF5E00]/30"
                      : r.status === "approved"
                      ? "bg-[#FF007F]/10 text-[#FF007F] border-[#FF007F]/30"
                      : "bg-white/5 text-white/50 border-white/10"
                  }`}
                >
                  {r.status}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Dialog open={payoutOpen} onOpenChange={setPayoutOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black tracking-tighter">
              Request payout
            </DialogTitle>
            <DialogDescription className="text-white/60">
              Cashing out{" "}
              <span className="text-gradient-neon font-semibold">${available.toFixed(2)}</span>.
              We'll DM you on Instagram once approved.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitPayout} className="space-y-4 py-2">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-white/60 mb-2 block">
                Your CashApp tag
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">$</span>
                <Input
                  data-testid="payout-cashapp-input"
                  required
                  value={payoutForm.cashapp_tag.replace(/^\$/, "")}
                  onChange={(e) =>
                    setPayoutForm({ ...payoutForm, cashapp_tag: e.target.value })
                  }
                  placeholder="yourtag"
                  className="bg-transparent border-white/10 text-white pl-7 focus:border-[#FF007F] focus:ring-1 focus:ring-[#FF007F]"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-white/60 mb-2 block">
                Your Instagram / note (optional)
              </label>
              <Textarea
                data-testid="payout-note-input"
                value={payoutForm.note}
                onChange={(e) => setPayoutForm({ ...payoutForm, note: e.target.value })}
                placeholder="@yourhandle so we can DM you back"
                maxLength={300}
                rows={3}
                className="bg-transparent border-white/10 text-white focus:border-[#FF007F] focus:ring-1 focus:ring-[#FF007F]"
              />
            </div>
            <button
              type="submit"
              data-testid="payout-submit-btn"
              disabled={submitting}
              className="w-full bg-neon text-white font-bold py-3 rounded-full hover:scale-[1.02] transition-transform glow-pink disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Submit request
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </main>
  );
};

export default function AffiliatePortal() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [affiliate, setAffiliate] = useState(null);
  const [view, setView] = useState("login");
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!token) {
      setChecking(false);
      return;
    }
    api
      .get("/affiliate/me", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setAffiliate(r.data))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      })
      .finally(() => setChecking(false));
  }, [token]);

  const onAuth = (t, aff) => {
    localStorage.setItem(TOKEN_KEY, t);
    setToken(t);
    setAffiliate(aff);
  };

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAffiliate(null);
    setView("login");
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#030303]">
        <Loader2 className="w-6 h-6 animate-spin text-[#FF007F]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      {token && affiliate ? (
        <Dashboard token={token} affiliate={affiliate} onLogout={onLogout} />
      ) : (
        <main className="flex-1 flex items-center justify-center px-6 py-20">
          <div className="w-full max-w-md">
            <div className="w-12 h-12 rounded-xl bg-[#FF007F]/10 grid place-items-center mx-auto mb-6">
              {view === "login" ? (
                <Lock className="w-5 h-5 text-[#FF007F]" />
              ) : (
                <Sparkles className="w-5 h-5 text-[#FF007F]" />
              )}
            </div>
            <h1 className="font-display text-3xl font-black tracking-tighter text-center mb-2">
              {view === "login" ? "Affiliate login" : "Become an affiliate"}
            </h1>
            <p className="text-sm text-white/50 text-center mb-8">
              {view === "login"
                ? "Log in to track your referrals and payouts."
                : "Pick your promo code. Earn $1–$30 per paid order."}
            </p>
            <div className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-8">
              {view === "login" ? (
                <LoginForm onAuth={onAuth} switchToRegister={() => setView("register")} />
              ) : (
                <RegisterForm onAuth={onAuth} switchToLogin={() => setView("login")} />
              )}
            </div>

            <div className="mt-6 bg-[#0A0A0A]/50 border border-white/5 rounded-xl p-5">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF5E00] mb-3 flex items-center gap-2">
                <Check className="w-3 h-3" /> How it works
              </div>
              <ol className="space-y-2 text-sm text-white/60">
                <li>1. Sign up and pick your promo code.</li>
                <li>2. Share your code or referral link with friends.</li>
                <li>3. They get $1 off, you earn $1–$30 per paid order.</li>
                <li>4. Request your payout via email once you hit $20.</li>
              </ol>
            </div>
          </div>
        </main>
      )}
      <Footer />
    </div>
  );
}
